import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import type { ResearchRunSummary } from '../types';
import { RUN_HISTORY_STATUS_LABELS } from '../constants/history';
import { DATE_TIME_FORMAT_OPTIONS, MILLISECONDS_PER_SECOND } from '../constants/quota';
import { glassStyle } from '../styles';

const SKELETON_ROWS = 4;

const STATUS_BADGE_CLASS: Record<string, string> = {
  processing: 'bg-[#468BFF]/10 text-[#468BFF]',
  completed: 'bg-green-100 text-green-700',
  failed: 'bg-[#FE363B]/10 text-[#FE363B]',
};

interface RunHistoryListProps {
  items: ResearchRunSummary[];
  isLoading: boolean;
  openingId: string | null;
  pendingDeleteId: string | null;
  onOpen: (id: string) => void;
  onRequestDelete: (id: string) => void;
  onCancelDelete: () => void;
  onConfirmDelete: (id: string) => void;
}

interface RunHistoryRowProps {
  item: ResearchRunSummary;
  isOpening: boolean;
  isPendingDelete: boolean;
  onOpen: () => void;
  onRequestDelete: () => void;
  onCancelDelete: () => void;
  onConfirmDelete: () => void;
}

function formatCreatedAt(createdAt: string): string {
  return new Intl.DateTimeFormat('pt-BR', DATE_TIME_FORMAT_OPTIONS).format(new Date(createdAt));
}

function RunHistoryRow({
  item,
  isOpening,
  isPendingDelete,
  onOpen,
  onRequestDelete,
  onCancelDelete,
  onConfirmDelete,
}: RunHistoryRowProps) {
  const statusLabel = RUN_HISTORY_STATUS_LABELS[item.status] || item.status;
  const durationSeconds =
    item.duration_ms !== null ? Math.round(item.duration_ms / MILLISECONDS_PER_SECOND) : null;

  return (
    <li className="px-4 py-3 border-b border-gray-200/50 last:border-b-0 flex flex-col sm:flex-row sm:items-start justify-between gap-3 font-['DM_Sans']">
      <div className="min-w-0 flex-1">
        <p className="font-medium text-gray-900 truncate" title={item.company}>
          {item.company}
        </p>
        <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-gray-500">
          <span className={`px-2 py-0.5 rounded-full ${STATUS_BADGE_CLASS[item.status] || 'bg-gray-100 text-gray-600'}`}>
            {statusLabel}
          </span>
          <span>{formatCreatedAt(item.created_at)}</span>
          {item.industry && <span>{item.industry}</span>}
          <span>{item.reference_count} referências</span>
          {durationSeconds !== null && <span>{durationSeconds}s</span>}
          {item.has_report && <span className="font-medium text-gray-700">Relatório</span>}
        </div>
      </div>

      <div className="flex items-center gap-4 shrink-0">
        {isPendingDelete ? (
          <>
            <span className="text-sm text-gray-600">Excluir?</span>
            <button
              type="button"
              onClick={onConfirmDelete}
              className="text-sm font-medium text-[#FE363B] hover:underline"
            >
              Confirmar
            </button>
            <button
              type="button"
              onClick={onCancelDelete}
              className="text-sm font-medium text-gray-600 hover:text-gray-900"
            >
              Cancelar
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={onOpen}
              disabled={isOpening}
              className="text-sm font-medium text-[#468BFF] hover:underline disabled:cursor-default disabled:opacity-60 inline-flex items-center gap-1.5"
            >
              {isOpening && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Abrir
            </button>
            <button
              type="button"
              onClick={onRequestDelete}
              disabled={isOpening}
              className="text-sm font-medium text-gray-600 hover:text-gray-900 disabled:cursor-default disabled:opacity-60"
            >
              Excluir
            </button>
          </>
        )}
      </div>
    </li>
  );
}

function RunHistorySkeleton() {
  return (
    <ul className="animate-pulse">
      {Array.from({ length: SKELETON_ROWS }, (_, index) => (
        <li key={index} className="px-4 py-4 border-b border-gray-200/50 last:border-b-0 space-y-2">
          <div className="h-4 bg-gray-200 rounded w-2/3" />
          <div className="h-3 bg-gray-200 rounded w-1/3" />
        </li>
      ))}
    </ul>
  );
}

const RunHistoryList = ({
  items,
  isLoading,
  openingId,
  pendingDeleteId,
  onOpen,
  onRequestDelete,
  onCancelDelete,
  onConfirmDelete,
}: RunHistoryListProps) => {
  return (
    <div className={`${glassStyle.base} rounded-2xl overflow-hidden bg-white/90`}>
      {items.length === 0 && isLoading ? (
        <RunHistorySkeleton />
      ) : items.length === 0 ? (
        <div className="px-4 py-10 text-center font-['DM_Sans']">
          <p className="text-sm text-gray-600">Nenhuma pesquisa encontrada.</p>
          <Link to="/" className="mt-3 inline-block text-sm font-medium text-[#468BFF] hover:underline">
            Iniciar nova pesquisa
          </Link>
        </div>
      ) : (
        <ul>
          {items.map((item) => (
            <RunHistoryRow
              key={item.id}
              item={item}
              isOpening={openingId === item.id}
              isPendingDelete={pendingDeleteId === item.id}
              onOpen={() => onOpen(item.id)}
              onRequestDelete={() => onRequestDelete(item.id)}
              onCancelDelete={onCancelDelete}
              onConfirmDelete={() => onConfirmDelete(item.id)}
            />
          ))}
        </ul>
      )}
    </div>
  );
};

export default RunHistoryList;
